import { DIGISAKA_API_ORIGIN } from '../../../config/digisakaApiOrigin';
import {
  digisakaAuthRequestInit,
  digisakaMeteredAuthHeaders,
  gptInferenceJsonHeaders,
} from '../../../config/digisakaGptInference';

export type MultitemporalZonalLayer = 'cadi' | 'pestRisk';

export interface ZonalStatResult {
  mean: number;
  min: number;
  max: number;
  validPercent: number;
}

export interface MunicipalityZonalRow {
  municipality: string;
  stats: ZonalStatResult | null;
  error?: string;
}

export type MultitemporalZonalPanelState =
  | { status: 'idle' }
  | { status: 'loading'; progress: number; total: number; message?: string }
  | {
      status: 'loaded';
      province: string;
      layer: MultitemporalZonalLayer;
      frameLabel: string;
      rows: MunicipalityZonalRow[];
      selectedMunicipality: string | null;
    }
  | { status: 'error'; message: string };

const STATS_MAX_SIZE = 512;

export async function fetchProvinces(): Promise<string[]> {
  const res = await fetch(
    `${DIGISAKA_API_ORIGIN}/api/provinces/`,
    digisakaAuthRequestInit({ headers: { ...digisakaMeteredAuthHeaders() } })
  );
  if (!res.ok) throw new Error('Failed to load provinces.');
  const data = await res.json();
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.provinces)) return data.provinces;
  return [];
}

export async function fetchMunicipalitiesByProvince(province: string): Promise<string[]> {
  const res = await fetch(
    `${DIGISAKA_API_ORIGIN}/api/municipalities/by-province/${encodeURIComponent(province)}`,
    digisakaAuthRequestInit({ headers: { ...digisakaMeteredAuthHeaders() } })
  );
  if (!res.ok) throw new Error('Failed to load municipalities.');
  const data = await res.json();
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.municipalities)) return data.municipalities;
  return [];
}

export async function fetchMunicipalityGeometry(
  province: string,
  municipality: string,
  signal?: AbortSignal
): Promise<GeoJSON.Geometry> {
  const params = new URLSearchParams({
    province_name: province,
    municipality_name: municipality,
  });
  const res = await fetch(
    `${DIGISAKA_API_ORIGIN}/api/municipalities/geometry/?${params}`,
    digisakaAuthRequestInit({ headers: { ...digisakaMeteredAuthHeaders() }, signal })
  );
  if (!res.ok) throw new Error(`Boundary not found for ${municipality}.`);
  const feat = (await res.json()) as GeoJSON.Feature;
  if (!feat.geometry) throw new Error(`Boundary not found for ${municipality}.`);
  return feat.geometry;
}

/** Province bounding box [minLon, minLat, maxLon, maxLat] for map zoom. */
export async function fetchProvinceBbox(
  province: string,
  signal?: AbortSignal
): Promise<[number, number, number, number]> {
  const params = new URLSearchParams({ province_name: province });
  const res = await fetch(
    `${DIGISAKA_API_ORIGIN}/api/provinces/geometry/?${params}`,
    digisakaAuthRequestInit({ headers: { ...digisakaMeteredAuthHeaders() }, signal })
  );
  if (!res.ok) throw new Error(`Boundary not found for province ${province}.`);
  const feat = (await res.json()) as GeoJSON.Feature & {
    bbox?: [number, number, number, number];
  };
  if (feat.bbox && feat.bbox.length === 4) {
    return [feat.bbox[0], feat.bbox[1], feat.bbox[2], feat.bbox[3]];
  }
  if (!feat.geometry) throw new Error(`Boundary not found for province ${province}.`);
  const ring =
    feat.geometry.type === 'Polygon'
      ? feat.geometry.coordinates[0]
      : feat.geometry.type === 'MultiPolygon'
        ? feat.geometry.coordinates[0]?.[0]
        : null;
  if (!ring?.length) throw new Error(`Boundary not found for province ${province}.`);
  let minLon = Infinity;
  let minLat = Infinity;
  let maxLon = -Infinity;
  let maxLat = -Infinity;
  for (const c of ring) {
    const lon = Number(c[0]);
    const lat = Number(c[1]);
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) continue;
    minLon = Math.min(minLon, lon);
    minLat = Math.min(minLat, lat);
    maxLon = Math.max(maxLon, lon);
    maxLat = Math.max(maxLat, lat);
  }
  if (!Number.isFinite(minLon)) throw new Error(`Boundary not found for province ${province}.`);
  return [minLon, minLat, maxLon, maxLat];
}

export async function fetchZonalStatForCog(
  cogUrl: string,
  geometry: GeoJSON.Geometry,
  signal?: AbortSignal,
  bidx = 1,
  nodata = 'nan'
): Promise<ZonalStatResult | null> {
  const params = new URLSearchParams({
    url: cogUrl,
    bidx: String(bidx),
    nodata,
    max_size: String(STATS_MAX_SIZE),
  });
  const body = {
    type: 'FeatureCollection',
    features: [{ type: 'Feature', geometry, properties: {} }],
  };

  const res = await fetch(
    `${DIGISAKA_API_ORIGIN}/api/statistics?${params}`,
    digisakaAuthRequestInit({
      method: 'POST',
      headers: { ...gptInferenceJsonHeaders(), ...digisakaMeteredAuthHeaders() },
      body: JSON.stringify(body),
      signal,
    })
  );

  if (!res.ok) return null;

  const data = await res.json();
  const featProps = data?.features?.[0]?.properties ?? data?.properties;
  const stats =
    featProps?.statistics?.[`b${bidx}`] ??
    featProps?.statistics?.b1 ??
    featProps?.statistics?.[String(bidx)] ??
    featProps?.statistics?.['1'];
  if (!stats) return null;

  const mean = Number(stats.mean);
  if (Number.isNaN(mean)) return null;
  // CADI is 0–1. A mean above 1 means fill value 255 was still included.
  if (nodata === '255' && (mean < 0 || mean > 1)) return null;

  return {
    mean,
    min: Number(stats.min ?? mean),
    max: Number(stats.max ?? mean),
    validPercent: Number(stats.valid_percent ?? 0),
  };
}

async function mapWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  fn: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let next = 0;

  async function worker() {
    while (next < items.length) {
      const i = next++;
      results[i] = await fn(items[i], i);
    }
  }

  const workers = Array.from({ length: Math.min(concurrency, items.length) }, () => worker());
  await Promise.all(workers);
  return results;
}

export async function fetchProvinceMunicipalityZonalStats(
  province: string,
  municipalities: string[],
  cogUrl: string,
  onProgress: (done: number, total: number) => void,
  signal?: AbortSignal,
  bidx = 1,
  nodata = 'nan'
): Promise<MunicipalityZonalRow[]> {
  const total = municipalities.length;
  let done = 0;

  const rows = await mapWithConcurrency(municipalities, 4, async (municipality) => {
    if (signal?.aborted) {
      return { municipality, stats: null, error: 'Cancelled' };
    }
    try {
      const geometry = await fetchMunicipalityGeometry(province, municipality, signal);
      const stats = await fetchZonalStatForCog(cogUrl, geometry, signal, bidx, nodata);
      return { municipality, stats };
    } catch (err: unknown) {
      const message = (err as Error)?.message ?? 'Failed';
      return { municipality, stats: null, error: message };
    } finally {
      done += 1;
      onProgress(done, total);
    }
  });

  return rows.sort((a, b) => a.municipality.localeCompare(b.municipality));
}
